import { __decorate, __param } from '../../../../../../node_modules/tslib/tslib.es6.js';
import * as dom from 'monaco-editor/esm/vs/base/browser/dom.js';
import * as aria from 'monaco-editor/esm/vs/base/browser/ui/aria/aria.js';
import { Emitter } from 'monaco-editor/esm/vs/base/common/event.js';
import { HistoryNavigator } from 'monaco-editor/esm/vs/base/common/history.js';
import { Disposable, DisposableStore } from 'monaco-editor/esm/vs/base/common/lifecycle.js';
import { URI } from 'monaco-editor/esm/vs/base/common/uri.js';
import './media/interactiveSession.css.js';
import { CodeEditorWidget } from 'monaco-editor/esm/vs/editor/browser/widget/codeEditorWidget.js';
import { IModelService } from 'monaco-editor/esm/vs/editor/common/services/model.js';
import { localize } from 'monaco-editor/esm/vs/nls.js';
import { MenuWorkbenchToolBar } from 'monaco-editor/esm/vs/platform/actions/browser/toolbar.js';
import { MenuId } from 'monaco-editor/esm/vs/platform/actions/common/actions.js';
import { IContextKeyService } from 'monaco-editor/esm/vs/platform/contextkey/common/contextkey.js';
import { registerAndCreateHistoryNavigationContext } from 'monaco-editor/esm/vs/platform/history/browser/contextScopedHistoryWidget.js';
import { IInstantiationService } from 'monaco-editor/esm/vs/platform/instantiation/common/instantiation.js';
import { ServiceCollection } from 'monaco-editor/esm/vs/platform/instantiation/common/serviceCollection.js';
import { DEFAULT_FONT_FAMILY } from '../../../browser/style.js';
import { getSimpleCodeEditorWidgetOptions, getSimpleEditorOptions } from '../../codeEditor/browser/simpleEditorOptions.js';
import { InteractiveSessionFollowups } from './interactiveSessionFollowups.js';
import { CONTEXT_INTERACTIVE_INPUT_HAS_TEXT, CONTEXT_IN_INTERACTIVE_INPUT } from '../common/interactiveSessionContextKeys.js';
import { IInteractiveSessionWidgetHistoryService } from '../common/interactiveSessionWidgetHistoryService.js';
const $ = dom.$;
const INPUT_EDITOR_MAX_HEIGHT = 250;
let InteractiveSessionInputPart = class InteractiveSessionInputPart extends Disposable {
    get inputEditor() {
        return this._inputEditor;
    }
    constructor(historyService, modelService, instantiationService, contextKeyService) {
        super();
        this.historyService = historyService;
        this.modelService = modelService;
        this.instantiationService = instantiationService;
        this.contextKeyService = contextKeyService;
        this._onDidChangeHeight = this._register(( new Emitter()));
        this.onDidChangeHeight = this._onDidChangeHeight.event;
        this._onDidFocus = this._register(( new Emitter()));
        this.onDidFocus = this._onDidFocus.event;
        this._onDidBlur = this._register(( new Emitter()));
        this.onDidBlur = this._onDidBlur.event;
        this._onDidAcceptFollowup = this._register(( new Emitter()));
        this.onDidAcceptFollowup = this._onDidAcceptFollowup.event;
        this.inputEditorHeight = 0;
        this.followupsDisposables = this._register(( new DisposableStore()));
        this.inputUri = ( URI.parse(
            `${InteractiveSessionInputPart.INPUT_SCHEME}:input-${InteractiveSessionInputPart._counter++}`
        ));
        this.inputEditorHasText = CONTEXT_INTERACTIVE_INPUT_HAS_TEXT.bindTo(contextKeyService);
        this.history = ( new HistoryNavigator([], 5));
        this._register(this.historyService.onDidClearHistory(() => this.history.clear()));
    }
    setState(providerId, inputValue) {
        this.providerId = providerId;
        const history = this.historyService.getHistory(providerId);
        this.history = ( new HistoryNavigator(history, 50));
        this.setValue(inputValue);
    }
    get element() {
        return this.container;
    }
    showPreviousValue() {
        this.navigateHistory(true);
    }
    showNextValue() {
        this.navigateHistory(false);
    }
    navigateHistory(previous) {
        const historyInput = (previous ?
            (this.history.previous() ?? this.history.first()) : this.history.next())
            ?? '';
        aria.status(historyInput);
        this.setValue(historyInput);
        this.setHistoryNavigationEnablement(true);
    }
    setValue(value) {
        this.inputEditor.setValue(value);
        this.inputEditor.setPosition({ lineNumber: 1, column: value.length + 1 });
    }
    focus() {
        this._inputEditor.focus();
    }
    async acceptInput(query) {
        const editorValue = this._inputEditor.getValue();
        if (!query && editorValue) {
            this.history.add(editorValue);
        }
        this._inputEditor.focus();
        this._inputEditor.setValue('');
    }
    render(container, initialValue, widget) {
        this.container = dom.append(container, $('.interactive-input-part'));
        this.followupsContainer = dom.append(this.container, $('.interactive-input-followups'));
        const inputContainer = dom.append(this.container, $('.interactive-input-and-toolbar'));
        const inputScopedContextKeyService = this._register(this.contextKeyService.createScoped(inputContainer));
        CONTEXT_IN_INTERACTIVE_INPUT.bindTo(inputScopedContextKeyService).set(true);
        const scopedInstantiationService = this.instantiationService.createChild(( new ServiceCollection([IContextKeyService, inputScopedContextKeyService])));
        const { historyNavigationBackwardsEnablement, historyNavigationForwardsEnablement } = this._register(registerAndCreateHistoryNavigationContext(inputScopedContextKeyService, this));
        this.setHistoryNavigationEnablement = enabled => {
            historyNavigationBackwardsEnablement.set(enabled);
            historyNavigationForwardsEnablement.set(enabled);
        };
        const options = getSimpleEditorOptions();
        options.readOnly = false;
        options.ariaLabel = ( localize('interactiveSessionInput', "Interactive Session Input"));
        options.fontFamily = DEFAULT_FONT_FAMILY;
        options.fontSize = 13;
        options.lineHeight = 20;
        options.padding = { top: 8, bottom: 7 };
        options.cursorWidth = 1;
        options.wrappingStrategy = 'advanced';
        options.bracketPairColorization = { enabled: false };
        options.suggest = { showIcons: false };
        options.scrollbar = { ...(options.scrollbar ?? {}), vertical: 'hidden' };
        const inputEditorElement = dom.append(inputContainer, $('.interactive-input-editor'));
        this._inputEditor = this._register(scopedInstantiationService.createInstance(CodeEditorWidget, inputEditorElement, options, getSimpleCodeEditorWidgetOptions()));
        this._register(this._inputEditor.onDidChangeModelContent(() => {
            const currentHeight = Math.min(this._inputEditor.getContentHeight(), INPUT_EDITOR_MAX_HEIGHT);
            if (currentHeight !== this.inputEditorHeight) {
                this.inputEditorHeight = currentHeight;
                this._onDidChangeHeight.fire();
            }
            const model = this._inputEditor.getModel();
            const inputHasText = !!model && model.getValue() !== '';
            this.setHistoryNavigationEnablement(!inputHasText);
            this.inputEditorHasText.set(inputHasText);
        }));
        this._register(this._inputEditor.onDidFocusEditorText(() => {
            this._onDidFocus.fire();
            inputContainer.classList.toggle('focused', true);
        }));
        this._register(this._inputEditor.onDidBlurEditorText(() => {
            inputContainer.classList.toggle('focused', false);
            this._onDidBlur.fire();
        }));
        const toolbar = this._register(this.instantiationService.createInstance(MenuWorkbenchToolBar, inputContainer, MenuId.InteractiveSessionExecute, {
            menuOptions: {
                shouldForwardArgs: true
            }
        }));
        toolbar.getElement().classList.add('interactive-execute-toolbar');
        toolbar.context = { widget };
        this.inputModel = this.modelService.getModel(this.inputUri) || this.modelService.createModel('', null, this.inputUri, true);
        this.inputModel.updateOptions({ bracketColorizationOptions: { enabled: false, independentColorPoolPerBracketType: false } });
        this._inputEditor.setModel(this.inputModel);
        if (initialValue) {
            this.inputModel.setValue(initialValue);
            const lineNumber = this.inputModel.getLineCount();
            this._inputEditor.setPosition({ lineNumber, column: this.inputModel.getLineMaxColumn(lineNumber) });
        }
    }
    async renderFollowups(items) {
        this.followupsDisposables.clear();
        dom.clearNode(this.followupsContainer);
        if (items && items.length > 0) {
            this.followupsDisposables.add(( new InteractiveSessionFollowups(
                this.followupsContainer,
                items,
                undefined,
                followup => this._onDidAcceptFollowup.fire(followup)
            )));
        }
    }
    layout(height, width) {
        return this._layout(height, width);
    }
    _layout(height, width, allowRecurse = true) {
        const followupsHeight = this.followupsContainer.offsetHeight;
        const inputPartBorder = 1;
        const inputPartPadding = 24;
        const inputEditorHeight = Math.min(this._inputEditor.getContentHeight(), height - followupsHeight - inputPartPadding - inputPartBorder, INPUT_EDITOR_MAX_HEIGHT);
        const inputEditorBorder = 2;
        const inputPartHeight = followupsHeight + inputEditorHeight + inputPartPadding + inputPartBorder + inputEditorBorder;
        const editorBorder = 2;
        const editorPadding = 8;
        const executeToolbarWidth = 25;
        const initialEditorScrollWidth = this._inputEditor.getScrollWidth();
        this._inputEditor.layout({ width: width - inputPartPadding - editorBorder - editorPadding - executeToolbarWidth, height: inputEditorHeight });
        if (allowRecurse && initialEditorScrollWidth < 10) {
            return this._layout(height, width, false);
        }
        return inputPartHeight;
    }
    saveState() {
        const inputHistory = this.history.getHistory();
        this.historyService.saveHistory(this.providerId, inputHistory);
    }
};
InteractiveSessionInputPart.INPUT_SCHEME = 'interactiveSessionInput';
InteractiveSessionInputPart._counter = 0;
InteractiveSessionInputPart = ( __decorate([
    ( __param(0, IInteractiveSessionWidgetHistoryService)),
    ( __param(1, IModelService)),
    ( __param(2, IInstantiationService)),
    ( __param(3, IContextKeyService))
], InteractiveSessionInputPart));
export { InteractiveSessionInputPart };
